import {apiPut, get, post, postForm} from '../../utils/requests';
import {SWFT_DIGITAL_API_HOST} from '../../constants';
import {
    FOLDER_SHARE_ENDPOINT,
    GET_MEDIA_ENDPOINT,
    SAVE_MEDIA_FILE_IMAGE_ENDPOINT,
    UPLOAD_MEDIA_ENDPOINT,
} from '../../constants/endpoints';
import Tracker from '../../lib/analytics/tracker';
import {ErrorTags, EventNames, EventProperty} from '../../lib/analytics/events';
import {createAsyncThunk} from '@reduxjs/toolkit';
import {mediaFileBulkUpload} from '../../reducers/media';
import {UploadRequest} from '../media';

interface SaveMediaImageFileRequest {
    orgUid: string;
    mediaFileUid: string;
    imageUrl: string;
}

export interface FolderPermission {
    orgUid: string;
    folderUid: string;
    userEmail: string;
    accessLevel: string;
    action: string;
}

export const MoveFiles = async (props: {fileUid: string; folderName: string; orgUid: string}) => {
    const body = {
        folder_name: props.folderName,
        org_uid: props.orgUid,
    };

    return await apiPut<any>(SWFT_DIGITAL_API_HOST + `/v1/media/${props.fileUid}/move`, body);
};

export const getFileMetaData = async (props: {orgUid: string; fileUid: string}) => {
    try {
        return await get(
            SWFT_DIGITAL_API_HOST + GET_MEDIA_ENDPOINT + '?org_uid=' + props.orgUid + '&file_uid=' + props.fileUid,
        );
    } catch (e: any) {
        Tracker.trackError(e, ErrorTags.GET_FILE_METADATA_ERROR, {
            orgUid: props.orgUid,
            fileUid: props.fileUid,
        });
        throw {failure: 'true'};
    }
};

export const saveImageMediaFile = async (params: SaveMediaImageFileRequest) => {
    try {
        const body = {
            media_file_uid: params.mediaFileUid,
            org_uid: params.orgUid,
            url: params.imageUrl,
        };
        const response = await post(SWFT_DIGITAL_API_HOST + SAVE_MEDIA_FILE_IMAGE_ENDPOINT, body);
        Tracker.trackEvent(EventNames.TRANSFORMATION_SAVED, {
            [EventProperty.TransformationFileUid]: response.media_image_uid,
            [EventProperty.FileUid]: response.media_file_uid,
            [EventProperty.TransformationFileUrl]: response.url,
            [EventProperty.FileUrl]: params.imageUrl,
        });

        return response;
    } catch (e: any) {
        Tracker.trackError(e, ErrorTags.SAVE_TRANSFORMATION_ERROR, {
            orgUid: params.orgUid,
            fileUid: params.mediaFileUid,
            fileUrl: params.imageUrl,
        });
        throw {failure: 'true'};
    }
};

export const folderPermissionChange = async (params: FolderPermission) => {
    try {
        const body = {
            org_uid: params.orgUid,
            folder_uid: params.folderUid,
            user_email: params.userEmail,
            access_level: params.accessLevel,
            action: params.action,
        };
        return await apiPut(SWFT_DIGITAL_API_HOST + FOLDER_SHARE_ENDPOINT, body);
    } catch (e: any) {
        throw {failure: 'true'};
    }
};

export const getImageFromDalle3 = async (prompt: string, style: string, size: string) => {
    try {
        let styleSelected = 'vivid';
        let styleSize = size;
        if (style === 'natural') {
            styleSelected = 'natural';
        }
        if (styleSize === 'none') {
            styleSize = '1024x1024';
        }
        const body = {
            prompt: prompt,
            style: styleSelected,
            quality: 'hd',
            size: styleSize,
        };
        return await post(SWFT_DIGITAL_API_HOST + '/media/generate-image', body);
    } catch (e: any) {
        throw {failure: 'true', error: e};
    }
};

export const uploadFile = async ({params}: {params: UploadRequest}) => {
    const apiEndPoint = SWFT_DIGITAL_API_HOST + UPLOAD_MEDIA_ENDPOINT;
    try {
        const formData = new FormData();
        formData.append('file', params.file);
        if (params.folder) {
            formData.append('folder', params.folder);
        }
        formData.append('name', params.fileName ? params.fileName : params.file.name);
        formData.append('size', params.file.size.toString());
        formData.append('type', params.file.type);
        formData.append('org_uid', params.orgUid);
        formData.append('uploaded_type', params.uploaded_type);
        const response = await postForm(apiEndPoint, formData);
        return response;
    } catch (e: any) {
        throw {failure: 'true'};
    }
};
